import Select, { SingleValue } from 'react-select';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import React, { useEffect, useState } from 'react';
import { DestinationType, FilterOptionsType, HtiEresSupplierType } from '../types.ts';
import { getArrayOfOptions } from '../../../helpers/getArrayOfOptions.ts';
import dayjs, { Dayjs } from 'dayjs';
import { styled } from 'styled-components';
import { StyledRow } from './AvailabilityTable/styled.ts';

const StyledFormGroup = styled.div`
  margin-bottom: 30px;

  .MuiFormControl-root {
    background: white;
  }
  .MuiInputBase-root {
    flex-direction: row-reverse;
  }
  .MuiInputBase-input {
    padding: 7px 12px;
    border-bottom: none;
    color: hsl(0, 0%, 20%);
    font-family: 'Open Sans', Arial, Helvetica, sans-serif;
    font-size: 14px;
  }
  .MuiOutlinedInput-notchedOutline {
    border-color: hsl(0, 0%, 80%);
  }
  .MuiInputBase-root:hover {
    .MuiOutlinedInput-notchedOutline {
      border-color: hsl(0, 0%, 70%);
    }
  }
  .Mui-focused {
    .MuiOutlinedInput-notchedOutline {
      border-color: #2684ff !important;
    }
  }
`;

const StyledFormLabel = styled.label`
  display: block;
  margin-bottom: 0;
  font-weight: 300;
  color: #212121;
  text-transform: uppercase;
`;

interface OptionType {
  label: string | null;
  value: number | null;
}

interface FilterProps {
  destinations?: DestinationType[];
  htiEresSuppliers?: HtiEresSupplierType[];
  filterOptions: FilterOptionsType;
  setFilterOptions: (filterOptions: FilterOptionsType) => void;
  handleGetHtiEresSuppliers?: (destinationId: number | null) => void;
}

const Filter: React.FC<FilterProps> = ({
  destinations = [],
  htiEresSuppliers = [],
  filterOptions,
  setFilterOptions,
  handleGetHtiEresSuppliers = () => {},
}) => {
  const [destinationsOptions, setDestinationsOptions] = useState<OptionType[]>([]);
  const [htiEresSuppliersOptions, setHtiEresSuppliersOptions] = useState<OptionType[]>([]);
  const nightsOptions: OptionType[] = getArrayOfOptions(7);
  const roomsOptions: OptionType[] = getArrayOfOptions(9);

  const getValidOptions = (arr: DestinationType[] | HtiEresSupplierType[]): OptionType[] => {
    return arr.map((el) => {
      return {
        value: el.Key,
        label: el.Value,
      };
    });
  };

  useEffect(() => {
    setDestinationsOptions(getValidOptions(destinations));
  }, [destinations]);

  useEffect(() => {
    setHtiEresSuppliersOptions(getValidOptions(htiEresSuppliers));
  }, [htiEresSuppliers]);

  const setSelectedStartDate = (date: Dayjs | null) => {
    if (date) {
      if (dayjs(date).format() !== 'Invalid Date') {
        const newFilterOptions = { ...filterOptions };
        newFilterOptions.StartDate = new Date(dayjs(date).format());
        setFilterOptions(newFilterOptions);
      }
    }
  };

  const setSelectedDestination = (
    option: SingleValue<{ value: number | null; label: string | null }>,
  ) => {
    if (option) {
      const newFilterOptions = { ...filterOptions };
      newFilterOptions.DestinationId = option.value;
      newFilterOptions.HtiEresSupplierId = null;
      setFilterOptions(newFilterOptions);
      handleGetHtiEresSuppliers(option.value);
    }
  };

  const setSelectedHtiEresSupplier = (
    option: SingleValue<{ value: number | null; label: string | null }>,
  ) => {
    if (option) {
      const newFilterOptions = { ...filterOptions };
      newFilterOptions.HtiEresSupplierId = option.value;
      setFilterOptions(newFilterOptions);
    }
  };

  const setSelectedNights = (
    option: SingleValue<{ value: number | null; label: string | null }>,
  ) => {
    if (option) {
      const newFilterOptions = { ...filterOptions };
      newFilterOptions.CountOfNights = option.value;
      setFilterOptions(newFilterOptions);
    }
  };

  const setSelectedRooms = (
    option: SingleValue<{ value: number | null; label: string | null }>,
  ) => {
    if (option) {
      const newFilterOptions = { ...filterOptions };
      newFilterOptions.CountOfRooms = option.value;
      setFilterOptions(newFilterOptions);
    }
  };

  return (
    <StyledRow>
      <div className="col-md-3">
        <StyledFormGroup>
          <StyledFormLabel>Destination:</StyledFormLabel>

          <Select
            onChange={setSelectedDestination}
            options={destinationsOptions}
            value={
              filterOptions.DestinationId
                ? destinationsOptions.find((el) => el.value === filterOptions.DestinationId)
                : { value: null, label: '- Show All -' }
            }
          />
        </StyledFormGroup>
      </div>

      <div className="col-md-3">
        <StyledFormGroup>
          <StyledFormLabel>Suppliers:</StyledFormLabel>

          <Select
            onChange={setSelectedHtiEresSupplier}
            options={htiEresSuppliersOptions}
            value={
              filterOptions.HtiEresSupplierId
                ? htiEresSuppliersOptions.find((el) => el.value === filterOptions.HtiEresSupplierId)
                : { value: null, label: filterOptions.DestinationId ? 'Select' : '- Show All -' }
            }
          />
        </StyledFormGroup>
      </div>

      <div className="col-md-2">
        <StyledFormGroup>
          <StyledFormLabel>Arrival Date:</StyledFormLabel>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              disablePast
              format="DD/MM/YYYY"
              value={dayjs(filterOptions.StartDate)}
              onChange={(newDate) => setSelectedStartDate(newDate)}
            />
          </LocalizationProvider>
        </StyledFormGroup>
      </div>

      <div className="col-md-2">
        <StyledFormGroup>
          <StyledFormLabel>Nights:</StyledFormLabel>
          <Select placeholder="Select" onChange={setSelectedNights} options={nightsOptions} />
        </StyledFormGroup>
      </div>

      <div className="col-md-2">
        <StyledFormGroup>
          <StyledFormLabel>Rooms:</StyledFormLabel>
          <Select placeholder="Select" onChange={setSelectedRooms} options={roomsOptions} />
        </StyledFormGroup>
      </div>
    </StyledRow>
  );
};

export default Filter;
